import { useState, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { ZoomIn } from 'lucide-react'
import { BottomSheet } from '@/components/ui/BottomSheet'
import { useIsCompact } from '@/hooks/useDevice'

interface ProductImageZoomProps {
  src: string
  alt: string
  // preview: enlarge whole image on hover. Default: 3x magnifier pan.
  preview?: boolean
}

const PREVIEW_SIZE = 400

/**
 * Desktop: hover shows an enlarged image (magnifier pan, or whole-image preview).
 * Compact (phone + tablet): tap opens the image in a bottom sheet with pinch-to-zoom.
 */
const ProductImageZoom = ({ src, alt, preview = false }: ProductImageZoomProps) => {
  const imgRef = useRef<HTMLDivElement>(null)
  const [zooming, setZooming] = useState(false)
  const [bgPos, setBgPos] = useState('center')
  const [zoomStyle, setZoomStyle] = useState<React.CSSProperties>({})
  const [sheetOpen, setSheetOpen] = useState(false)
  const isCompact = useIsCompact()

  const zoomLevel = 3
  const zoomSize = 450

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isCompact) {
        return
      }
      setZooming(true)
      if (preview) {
        // Position beside the thumbnail, flipping/clamping to stay in the viewport.
        const rect = e.currentTarget.getBoundingClientRect()
        let left = rect.right + 12
        if (left + PREVIEW_SIZE > window.innerWidth - 8) {
          const flipped = rect.left - PREVIEW_SIZE - 12
          left = flipped >= 8 ? flipped : Math.max(8, window.innerWidth - PREVIEW_SIZE - 8)
        }
        const top = Math.max(
          8,
          Math.min(rect.top + rect.height / 2 - PREVIEW_SIZE / 2, window.innerHeight - PREVIEW_SIZE - 8)
        )
        setZoomStyle({ position: 'fixed', top, left, width: PREVIEW_SIZE, height: PREVIEW_SIZE })
      }
    },
    [isCompact, preview]
  )

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (preview) {
        return
      }
      const img = e.currentTarget.querySelector('img')
      const rect = img ? img.getBoundingClientRect() : e.currentTarget.getBoundingClientRect()
      const containerRect = e.currentTarget.getBoundingClientRect()

      // Cursor position as ratio (0-1) within the actual image
      const ratioX = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
      const ratioY = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height))

      // Calculate pixel offset so the cursor point is centered in the popup
      const bgW = rect.width * zoomLevel
      const bgH = rect.height * zoomLevel
      const offsetX = -(ratioX * bgW - zoomSize / 2)
      const offsetY = -(ratioY * bgH - zoomSize / 2)

      setBgPos(`${offsetX}px ${offsetY}px`)

      setZoomStyle({
        position: 'fixed' as const,
        top: Math.max(containerRect.top - 120, 10),
        left: containerRect.right - 40,
        width: zoomSize,
        height: zoomSize,
        backgroundSize: `${bgW}px ${bgH}px`,
      })
    },
    [preview]
  )

  return (
    <>
      <div ref={imgRef} className="relative w-full h-full">
        <div
          className="w-full h-full cursor-zoom-in"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={() => setZooming(false)}
          onMouseMove={handleMouseMove}
          onClick={() => isCompact && setSheetOpen(true)}>
          <img src={src} alt={alt} className="max-h-full max-w-full object-contain rounded-lg mx-auto" />
          {isCompact && (
            <div className="absolute bottom-2 right-2 w-8 h-8 rounded-full bg-black/30 flex items-center justify-center">
              <ZoomIn size={16} className="text-white" />
            </div>
          )}
        </div>
      </div>

      {zooming &&
        !isCompact &&
        createPortal(
          preview ? (
            <div
              className="rounded-[5px] shadow-xl border overflow-hidden z-100 pointer-events-none"
              style={{
                ...zoomStyle,
                borderColor: 'var(--border-color, #e5e7eb)',
                backgroundColor: 'var(--content-background-color)',
              }}>
              <img src={src} alt={alt} className="w-full h-full object-contain rounded-[5px]" />
            </div>
          ) : (
            <div
              className="rounded-xl shadow-xl border overflow-hidden z-100 pointer-events-none"
              style={{
                ...zoomStyle,
                backgroundImage: `url(${src})`,
                backgroundPosition: bgPos,
                backgroundRepeat: 'no-repeat',
                borderColor: 'var(--border-color, #e5e7eb)',
                backgroundColor: 'var(--content-background-color)',
              }}
            />
          ),
          document.body
        )}

      <BottomSheet isOpen={sheetOpen} onClose={() => setSheetOpen(false)} title="Product Image">
        <div className="flex items-center justify-center py-4 overflow-auto" style={{ touchAction: 'pinch-zoom' }}>
          <img src={src} alt={alt} className="max-w-full max-h-[70vh] object-contain rounded-[5px]" />
        </div>
      </BottomSheet>
    </>
  )
}

export default ProductImageZoom
